import { MdLockOutline } from "react-icons/md";
import { submitPasswordReset } from "../../utils/api/endpoints/user/authentication";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import Form from "../forms/Form";
import AuthTextBox from "./inputs/AuthTextBox";
import { UseFormSetError } from "react-hook-form";
import AuthButton from "./inputs/AuthButton";
import { useMemo } from "react";

type ResetPasswordFields = {
  password: string;
  confirmPassword: string;
};

const ResetPasswordForm = ({
  email,
  token
}: {
  email: string;
  token: string;
}) => {
  const navigate = useNavigate();

  const handleSubmit = async (
    data: ResetPasswordFields,
    setError: UseFormSetError<ResetPasswordFields>
  ) => {
    if (data.password !== data.confirmPassword) {
      setError("confirmPassword", {
        message: "Passwords do not match"
      });

      return;
    }

    submitPasswordReset(data.password, email, token)
      .then(() => {
        toast.success("Password Changed Successfuly!");
        navigate("/login", { replace: true });
      })
      .catch(err => toast.error("Something went wrong..."));
  };

  const defaultValues = useMemo(
    () =>
      ({
        password: "",
        confirmPassword: ""
      }) as ResetPasswordFields,
    []
  );

  return (
    <article className="mx-auto w-full max-w-[480px]">
      {/* Header brand removed per request */}
      {/**
      <div className="mb-8 text-center">
        <Link to="/" className="inline-flex items-center gap-3 transition-opacity hover:opacity-80">
          <img src="/ctb-logo-crop.svg" alt="CTB Logo" className="h-12 w-auto" />
          <span className="text-2xl font-semibold text-gray-900">Capture The Bug</span>
        </Link>
      </div>
      */}

      {/* Clean white card */}
      <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm md:p-10">
        {/* Centered brand logo instead of lock icon */}
        <div className="mb-6 flex justify-center">
          <img src="/ctb-logo-crop.svg" alt="Capture The Bug" className="h-16 w-auto" />
        </div>

        {/* Header Section */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">
            Set New Password
          </h1>
          <p className="text-sm text-gray-600">
            Please enter a strong password for your account. Your password should be secure and memorable.
          </p>
        </div>

        <Form
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          className="space-y-5"
        >
          <AuthTextBox
            label="New Password"
            name="password"
            password={true}
            placeholder="Enter your new password"
            Icon={MdLockOutline}
            rules={{
              required: true,
              pattern: {
                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                message:
                  "Password must contain at least 8 characters, including uppercase, lowercase, number, and special character."
              }
            }}
          />

          <AuthTextBox
            label="Confirm Password"
            name="confirmPassword"
            password={true}
            placeholder="Confirm your new password"
            Icon={MdLockOutline}
            rules={{
              required: true
            }}
          />

          {/* Password Requirements */}
          <div className="rounded-md bg-blue-50 p-4">
            <h4 className="mb-2 text-sm font-medium text-blue-900">Password Requirements:</h4>
            <ul className="space-y-1 text-xs text-blue-700">
              <li>• At least 8 characters long</li>
              <li>• Contains uppercase and lowercase letters</li>
              <li>• Contains at least one number</li>
              <li>• Contains at least one special character (@$!%*?&)</li>
            </ul>
          </div>

          {/* Submit Button - Clean professional design */}
          <button
            type="submit"
            className="w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Update Password
          </button>
        </Form>

        {/* Footer */}
        <div className="mt-6 border-t border-gray-100 pt-6 text-center">
          <p className="text-sm text-gray-600">
            Remember your password?{" "}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </article>
  );
};

export default ResetPasswordForm;
